import React, { useEffect, useState, useMemo } from "react";
import { useSession, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useSemesterFilter } from "../hooks/useSemesterFilter";
import SemesterFilterDropdown from "../components/SemesterFilterDropdown";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Select,
  Button,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Collapse,
  Checkbox,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";

const STANDARD_PRIMARY_PATHOLOGIES = [
  "Abdominal pain/problems",
  "Allergic Reaction",
  "Altered Mental State",
  "Behavioral/psychiatric",
  "Burns",
  "Cardiac - Acute Coronary Syndrome",
  "Cardiac - Arrest",
  "Cardiac - Other",
  "Diabetic symptoms",
  "Electrocution",
  "GI Bleed",
  "Healthy Screening/Physical",
  "Hypovolemia/shock",
  "Neuro - Other",
  "Neuro - Seizure",
  "Neuro - Stroke/CVA",
  "Neuro - TIA",
  "OB-Birth C Sect.",
  "OB-Birth Vaginal",
  "OB-GYN",
  "OB-Labor",
  "OB-Pregnancy Probs.",
  "Observation Only",
  "Obvious death",
  "Other Medical",
  "Overdose/Poison",
  "Respiratory",
  "Sepsis/Infection",
  "Smoke inhalation",
  "Stings/venomous bites",
  "Syncope/fainting",
  "Trauma - Abdominal",
  "Trauma - Chest",
  "Trauma - Extremities",
  "Trauma - Head",
  "Trauma - Multisystems",
  "Trauma - Neck/Back",
  "Trauma - Unspecified",
];

const PRIMARY_PATHOLOGY_LOOKUP = new Map(
  STANDARD_PRIMARY_PATHOLOGIES.map((label) => [label.toLowerCase(), label])
);

// Helper function to extract shift location from various sources
const getShiftLocation = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.location) {
        return parsed.shift.location;
      }
    }
    if (shift.fisdapData?.location) {
      return shift.fisdapData.location;
    }
    if (shift.structuredData?.shift_location) {
      return shift.structuredData.shift_location;
    }
  } catch (e) {
    console.error("Error extracting location:", e);
  }
  return "Unknown Location";
};

// Helper function to extract patient count
const getPatientCount = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.document_info?.total_patients) {
        return parsed.shift.document_info.total_patients;
      }
    }
    if (shift.structuredData?.patients?.length) {
      return shift.structuredData.patients.length;
    }
    if (shift.patientEncounterCount) {
      return shift.patientEncounterCount;
    }
  } catch (e) {
    console.error("Error extracting patient count:", e);
  }
  return 0;
};

const parseEvaluationJson = (shift) => {
  try {
    const raw = shift?.aiEvaluationResponse?.markdown;
    if (!raw || typeof raw !== "string") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const canonicalizePathology = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return null;
  if (PRIMARY_PATHOLOGY_LOOKUP.has(normalized)) return PRIMARY_PATHOLOGY_LOOKUP.get(normalized);
  for (const [key, label] of PRIMARY_PATHOLOGY_LOOKUP.entries()) {
    if (normalized.includes(key) || key.includes(normalized)) return label;
  }
  return null;
};

const extractPrimaryImpressionFromNarrative = (narrative) => {
  const text = String(narrative || "");
  if (!text.trim()) return null;
  const match = text.match(/primary impression\s*:\s*([^\n\r]+)/i);
  return match?.[1] ? canonicalizePathology(match[1]) : null;
};

// Helper function to extract and aggregate impressions
const extractImpressions = (shifts, impressionType) => {
  const impressions = {};
  
  shifts.forEach((shift) => {
    try {
      // Try parsing from aiEvaluationResponse first
      let patients = [];
      
      if (shift.aiEvaluationResponse?.markdown) {
        try {
          const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
          if (parsed?.student_report?.per_patient_feedback) {
            patients = parsed.student_report.per_patient_feedback;
          }
        } catch (e) {
          console.error("Error parsing AI evaluation:", e);
        }
      }
      
      // Fallback to structuredData
      if (patients.length === 0 && shift.structuredData?.patients) {
        patients = shift.structuredData.patients;
      }
      
      patients.forEach((patient) => {
        const impression = patient[impressionType];
        if (impression) {
          impressions[impression] = (impressions[impression] || 0) + 1;
        }
      });
    } catch (e) {
      console.error("Error extracting impressions:", e);
    }
  });

  // Sort by frequency and return top 3
  return Object.entries(impressions)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([impression]) => impression);
};

// Format date helper
const formatDate = (date) => {
  if (!date) return "N/A";
  try {
    return new Date(date).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    return "N/A";
  }
};

// Helper to extract the actual shift date (not submission date)
const getShiftDate = (shift) => {
  try {
    // Parse the markdown JSON if it exists
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      if (parsed?.shift?.document_info?.shift_date) {
        // The shift date is in format like "Oct 22, 2025"
        const dateStr = parsed.shift.document_info.shift_date;
        // Convert to a proper date object
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate)) {
          return parsedDate.toISOString();
        }
        return dateStr; // Return as-is if parsing fails
      }
    }
    if (shift.fisdapData?.shift_date) {
      return shift.fisdapData.shift_date;
    }
    if (shift.structuredData?.shift_date) {
      return shift.structuredData.shift_date;
    }
    // Fallback to submission date if shift date not found
    if (shift.submissionDate) {
      return shift.submissionDate;
    }
  } catch (e) {
    console.error("Error extracting shift date:", e);
  }
  return shift.submissionDate || null;
};

// Helper to check if shift is locked
const isShiftLocked = (shift) => {
  try {
    if (shift.aiEvaluationResponse?.markdown) {
      const parsed = JSON.parse(shift.aiEvaluationResponse.markdown);
      return parsed?.shift?.shift_locked === true;
    }
    if (shift.fisdapData?.shift_locked) {
      return shift.fisdapData.shift_locked === true;
    }
    if (shift.structuredData?.shift_locked) {
      return shift.structuredData.shift_locked === true;
    }
  } catch (e) {
    console.error("Error checking shift lock status:", e);
  }
  return false;
};

// Helper to get duplicate info for a shift
const getDuplicateInfo = (shift, allShifts) => {
  if (!shift.sourceShiftId) return null;
  
  // Find all shifts with the same sourceShiftId
  const duplicates = allShifts.filter(
    s => s.sourceShiftId === shift.sourceShiftId
  );
  
  if (duplicates.length <= 1) return null;
  
  // Sort by submission date (oldest first)
  const sorted = [...duplicates].sort((a, b) => 
    new Date(a.submissionDate) - new Date(b.submissionDate)
  );
  
  // Find this shift's position
  const position = sorted.findIndex(s => s.id === shift.id) + 1;
  const isLatest = position === sorted.length;
  
  return {
    count: duplicates.length,
    position,
    isLatest
  };
};

// Status badge component
const StatusBadge = ({ verdict }) => {
  const colorScheme = {
    pass: "green",
    proficient: "green",
    acceptable: "green",
    exemplary: "green",
    fail: "red",
    needs_review: "yellow",
    required: "orange",
    indeterminate: "gray",
  };

  const label = {
    pass: "Pass",
    proficient: "Proficient",
    acceptable: "Acceptable",
    exemplary: "Exemplary",
    fail: "Fail",
    needs_review: "Needs Review",
    required: "Required",
    indeterminate: "Indeterminate",
  };

  return (
    <Badge colorScheme={colorScheme[verdict] || "gray"} size="sm">
      {label[verdict] || verdict}
    </Badge>
  );
};

// Shift detail modal
const ShiftDetailModal = ({ shift, relatedUserFiles = [], loadingRelatedFiles = false, isOpen, onClose }) => {
  if (!shift) return null;

  const sortedFiles = [...(relatedUserFiles || [])].sort((a, b) => {
    const aName = String(a?.originalFilename || a?.title || "").toLowerCase();
    const bName = String(b?.originalFilename || b?.title || "").toLowerCase();
    const aIsShiftReport = aName.includes("shift") && aName.includes("report");
    const bIsShiftReport = bName.includes("shift") && bName.includes("report");
    if (aIsShiftReport && !bIsShiftReport) return -1;
    if (!aIsShiftReport && bIsShiftReport) return 1;
    return aName.localeCompare(bName);
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Shift Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontWeight="bold">Source Shift ID:</Text>
              <Text>{shift.sourceShiftId}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Submission Date:</Text>
              <Text>{formatDate(shift.submissionDate)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Location:</Text>
              <Text>{getShiftLocation(shift)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Patient Count:</Text>
              <Text>{getPatientCount(shift)}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">Status:</Text>
              <StatusBadge verdict={shift.auditVerdict} />
            </Box>
            <Box>
              <Text fontWeight="bold">Attachments:</Text>
              {loadingRelatedFiles ? (
                <Spinner size="sm" mt={2} />
              ) : sortedFiles.length === 0 ? (
                <Text color="gray.500">No attachments found for this shift.</Text>
              ) : (
                <VStack align="stretch" spacing={2} mt={2}>
                  {sortedFiles.map((file) => (
                    <HStack key={file.id} justify="space-between" p={2} borderWidth={1} borderRadius="md">
                      <Text fontSize="sm">
                        {file.originalFilename || file.title || "Attachment"}
                      </Text>
                      {file.file?.url ? (
                        <Button
                          as="a"
                          href={file.file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                        >
                          Open
                        </Button>
                      ) : (
                        <Badge colorScheme="gray">No URL</Badge>
                      )}
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
            {shift.aiEvaluationResponse?.markdown && (
              <Box>
                <Text fontWeight="bold">AI Feedback:</Text>
                <Box
                  p={3}
                  bg="gray.800"
                  borderRadius="md"
                  maxH="300px"
                  overflowY="auto"
                  borderWidth={1}
                  borderColor="gray.600"
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.100">
                    {shift.aiEvaluationResponse.markdown}
                  </Text>
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    #printable-report, #printable-report * {
      visibility: visible;
    }
    #printable-report {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px;
    }
    .print-column {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
`;

const buildStudentDisplayName = (membership) => {
  if (membership?.user?.firstName || membership?.user?.lastName) {
    return `${membership.user?.firstName || ""} ${membership.user?.lastName || ""}`.trim();
  }
  return membership?.userEmail || "Unknown Student";
};

const sortStudentMemberships = (memberships = []) => {
  return [...memberships].sort((a, b) => {
    const aLast = String(a?.user?.lastName || "").toLowerCase();
    const bLast = String(b?.user?.lastName || "").toLowerCase();
    if (aLast !== bLast) return aLast.localeCompare(bLast);

    const aFirst = String(a?.user?.firstName || "").toLowerCase();
    const bFirst = String(b?.user?.firstName || "").toLowerCase();
    if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

    const aEmail = String(a?.user?.email || a?.userEmail || "").toLowerCase();
    const bEmail = String(b?.user?.email || b?.userEmail || "").toLowerCase();
    return aEmail.localeCompare(bEmail);
  });
};

const getSubmissionDateValue = (shift) => {
  return shift?.submissionDate || shift?.createdAt || null;
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isPassingShift = (shift) => {
  const verdict = String(shift?.auditVerdict || "").toLowerCase();
  const evaluation = String(shift?.evaluationStatus || "").toLowerCase();
  const passing = new Set(["pass", "proficient", "acceptable", "exemplary"]);
  return passing.has(verdict) || passing.has(evaluation);
};

const getShiftStudentId = (shift) => {
  return shift?.submittedById || shift?.submittedBy?.id || null;
};

const EMPTY_SHIFT_TYPE_COUNTS = {
  lab: 0,
  clinical: 0,
  field: 0,
  teamLead: 0,
  total: 0,
};

const getShiftTypeKey = (shift) => {
  const parsed = parseEvaluationJson(shift);
  const normalize = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const includesAny = (values, needles) =>
    values.some((value) => needles.some((needle) => value.includes(needle)));

  const typeCandidates = [
    shift?.shiftType,
    parsed?.shift?.shift_type,
    parsed?.shift?.document_info?.shift_type,
    shift?.structuredData?.shift_type,
    shift?.structuredData?.shift?.shift_type,
    shift?.fisdapData?.shift_type,
    shift?.emailMetadata?.shiftType,
  ]
    .map(normalize)
    .filter(Boolean);

  const locationCandidates = [
    parsed?.shift?.location,
    shift?.structuredData?.shift_location,
    shift?.structuredData?.location,
    shift?.fisdapData?.location,
    shift?.emailMetadata?.location,
    getShiftLocation(shift),
  ]
    .map(normalize)
    .filter(Boolean);

  const unitCandidates = [
    parsed?.shift?.unit,
    shift?.structuredData?.shift?.unit,
    shift?.structuredData?.unit,
    shift?.fisdapData?.unit,
    shift?.emailMetadata?.unit,
  ]
    .map(normalize)
    .filter(Boolean);

  const allCandidates = [...typeCandidates, ...locationCandidates, ...unitCandidates];

  const isTeamLead =
    shift?.structuredData?.shift?.team_lead_required === true ||
    shift?.fisdapData?.team_lead_required === true ||
    parsed?.shift?.shift_team_lead_required === true ||
    includesAny(allCandidates, ["team lead", "teamlead"]);

  if (isTeamLead) return "teamLead";

  // Prefer explicit type identifiers from calendar/FISDAP payloads first.
  if (includesAny(typeCandidates, ["lab", "skills", "classroom", "simulation"])) return "lab";
  if (includesAny(typeCandidates, ["clinical", "hospital"])) return "clinical";
  if (includesAny(typeCandidates, ["field", "ride along", "ems", "fire"])) return "field";

  // Fallback to location/unit-derived heuristics if type isn't available.
  if (includesAny(allCandidates, ["skills", "classroom", "simulation", "sim lab", " paramedic lab"])) return "lab";
  if (includesAny(allCandidates, ["hospital", " clinic", " er", " ed", "icu", "pediatric", "women", "children", "medical center"])) return "clinical";
  if (includesAny(allCandidates, ["fire department", " ems", "ambulance", "rescue", "engine", "truck", "mfd", "scfd", "medic"])) return "field";

  return null;
};

const getShiftTypeLabel = (shift) => {
  const typeKey = getShiftTypeKey(shift);
  const labels = {
    lab: "Lab",
    clinical: "Clinical",
    field: "Field",
    teamLead: "Team Lead",
  };
  return labels[typeKey] || "Unknown";
};

const computeStudentPathologyCounts = (shifts = []) => {
  const basePrimary = {};
  const baseSecondary = {};
  for (const label of STANDARD_PRIMARY_PATHOLOGIES) {
    basePrimary[label] = 0;
    baseSecondary[label] = 0;
  }

  const result = {
    primaryCounts: basePrimary,
    secondaryCounts: baseSecondary,
    refusalCount: 0,
  };

  shifts.forEach((shift) => {
    if (!isPassingShift(shift)) return;

    const persistedSummary = shift?.aiEvaluationMetadata?.pathologySummary;
    if (persistedSummary?.primaryCounts && persistedSummary?.secondaryCounts) {
      for (const label of STANDARD_PRIMARY_PATHOLOGIES) {
        result.primaryCounts[label] += Number(persistedSummary.primaryCounts[label] || 0);
        result.secondaryCounts[label] += Number(persistedSummary.secondaryCounts[label] || 0);
      }
      result.refusalCount += Number(persistedSummary.refusalCount || 0);
      return;
    }

    const parsed = parseEvaluationJson(shift);
    const fromFeedback = parsed?.student_report?.per_patient_feedback || [];
    if (Array.isArray(fromFeedback) && fromFeedback.length > 0) {
      fromFeedback.forEach((patient) => {
        const primary = canonicalizePathology(patient?.primary_impression);
        const secondary = canonicalizePathology(patient?.secondary_impression);
        if (primary) result.primaryCounts[primary] += 1;
        if (secondary) result.secondaryCounts[secondary] += 1;
      });
    }

    const patients = Array.isArray(shift?.structuredData?.patients) ? shift.structuredData.patients : [];
    patients.forEach((patient) => {
      const text = String(patient?.narrative || "").toLowerCase();
      if (/refusal|refused|against medical advice|ama|signed refusal/.test(text)) {
        result.refusalCount += 1;
      }
    });
  });

  return result;
};

const summarizeStudentShifts = (shifts = []) => {
  return shifts.reduce(
    (acc, shift) => {
      const key = getShiftTypeKey(shift);
      if (key) acc.typeCounts[key] += 1;
      acc.total += 1;
      if (isPassingShift(shift)) acc.pass += 1;
      return acc;
    },
    {
      pass: 0,
      total: 0,
      typeCounts: { ...EMPTY_SHIFT_TYPE_COUNTS },
    }
  );
};

const selectLatestPassingShiftSubmissions = (shifts = []) => {
  const grouped = new Map();

  shifts.forEach((shift) => {
    const key = shift?.sourceShiftId || shift?.id;
    if (!key) return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(shift);
  });

  return Array.from(grouped.values())
    .map((group) =>
      group
        .filter((shift) => isPassingShift(shift))
        .sort((a, b) => {
          const bySubmission = toTimestamp(b.submissionDate) - toTimestamp(a.submissionDate);
          if (bySubmission !== 0) return bySubmission;
          const byUpdated = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
          if (byUpdated !== 0) return byUpdated;
          return String(b.id).localeCompare(String(a.id));
        })[0] || null
    )
    .filter(Boolean);
};

const applyAdvancedShiftFilters = (shifts = [], showDuplicates, showDrafts) => {
  let result = [...shifts];

  if (!showDrafts) {
    result = result.filter((shift) => {
      if (shift.isDraft === true) return false;
      return isShiftLocked(shift);
    });
  }

  if (!showDuplicates) {
    const grouped = {};
    result.forEach((shift) => {
      const key = shift.sourceShiftId || shift.id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(shift);
    });

    result = Object.values(grouped).map((group) =>
      group.sort((a, b) => {
        const bySubmission = toTimestamp(b.submissionDate) - toTimestamp(a.submissionDate);
        if (bySubmission !== 0) return bySubmission;
        const byCreated = toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
        if (byCreated !== 0) return byCreated;
        const byUpdated = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
        if (byUpdated !== 0) return byUpdated;
        return String(b.id).localeCompare(String(a.id));
      })[0]
    );
  }

  return result;
};

const applyDateRangeFilter = (shifts = [], dateRange) => {
  const bounds = getDateRangeBounds(dateRange);
  if (!bounds.from && !bounds.to) return [...shifts];

  const fromTs = bounds.from ? new Date(bounds.from).getTime() : null;
  const toTs = bounds.to ? new Date(bounds.to).getTime() : null;

  return shifts.filter((shift) => {
    const shiftDateValue = getShiftDate(shift) || getSubmissionDateValue(shift);
    const ts = toTimestamp(shiftDateValue);
    if (!ts) return false;
    if (fromTs && ts < fromTs) return false;
    if (toTs && ts > toTs) return false;
    return true;
  });
};

const getDateRangeBounds = (dateRange) => {
  const bounds = { from: null, to: null };
  if (dateRange?.from) {
    const from = new Date(`${dateRange.from}T00:00:00`);
    if (!Number.isNaN(from.getTime())) bounds.from = from.toISOString();
  }
  if (dateRange?.to) {
    const to = new Date(`${dateRange.to}T23:59:59.999`);
    if (!Number.isNaN(to.getTime())) bounds.to = to.toISOString();
  }
  return bounds;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatSemesterBadge = (semester) => {
  if (!semester) return "All semesters";
  const start = toDateInputValue(semester.startDate);
  const end = toDateInputValue(semester.endDate);
  if (start && end) return `${semester.name} (${start} to ${end})`;
  return semester.name || "Selected semester";
};

const SHIFT_REPORT_SELECT = {
  id: true,
  submittedById: true,
  sourceShiftId: true,
  submissionDate: true,
  submissionEmail: true,
  originalFilename: true,
  auditVerdict: true,
  requiresReview: true,
  evaluationStatus: true,
  patientEncounterCount: true,
  shiftType: true,
  isDraft: true,
  submittedBy: {
    id: true,
    firstName: true,
    lastName: true,
    email: true,
  },
  aiEvaluationResponse: {
    markdown: true
  },
  aiEvaluationMetadata: true,
  structuredData: true,
  fisdapData: true,
  emailMetadata: true,
  createdAt: true,
  updatedAt: true,
};

const fetchAllShiftPages = async (filter) => {
  const records = [];
  let after;

  while (true) {
    const page = await api.shiftProcessing.findMany({
      filter,
      select: SHIFT_REPORT_SELECT,
      sort: { submissionDate: "Descending" },
      first: 250,
      after,
    });

    records.push(...page);

    if (!page?.hasNextPage || !page?.endCursor) break;
    after = page.endCursor;
  }

  return records;
};

export default function FisdapReports({ studentOnly = false } = {}) {
  const navigate = useNavigate();
  const session = useSession(api);
  const [{ data: accessProfile }, getCurrentUserAccessProfile] = useGlobalAction(
    api.getCurrentUserAccessProfile
  );
  const sessionUserId = session?.user?.id;
  const sessionFallbackCohortId =
    session?.user?.cohort?.id ||
    session?.user?.cohortId ||
    session?.user?.invitationData?.cohortId ||
    "";
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [selectedShift, setSelectedShift] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isPathologyOpen,
    onOpen: onPathologyOpen,
    onClose: onPathologyClose,
  } = useDisclosure();
  const [selectedPathologyStudent, setSelectedPathologyStudent] = useState(null);
  const [pathologySemesterId, setPathologySemesterId] = useState("");
  const [expandedStudentShifts, setExpandedStudentShifts] = useState([]);
  const [fetchingExpandedShifts, setFetchingExpandedShifts] = useState(false);
  const [allShifts, setAllShifts] = useState([]);
  const [fetchingAllShifts, setFetchingAllShifts] = useState(false);
  const {
    semesters,
    selectedSemesterId,
    setSelectedSemesterId,
    semesterDateRange,
    loading: semestersLoading,
  } = useSemesterFilter(selectedCohortId);
  const selectedSemester = useMemo(
    () => semesters.find((s) => s.id === selectedSemesterId) || null,
    [semesters, selectedSemesterId]
  );

  // Check user access
  const userRoles = session?.user?.roles || [];
  const isAdmin =
    userRoles.some((role) => role.key === "admin") ||
    userRoles.some((role) => role.key === "system-admin");

  useEffect(() => {
    if (!sessionUserId) return;
    getCurrentUserAccessProfile({});
  }, [sessionUserId, getCurrentUserAccessProfile]);

  // Fetch user's cohort memberships to determine report access
  const [{ data: userMemberships, fetching: fetchingMemberships, error: membershipError }] =
    useFindMany(api.cohortMembership, {
      filter: {
        userId: { equals: session?.user?.id },
        active: { equals: true },
      },
      select: {
        id: true,
        cohortId: true,
        hasFisdapReviewAccess: true,
        primaryRole: true,
        cohort: {
          id: true,
          institution: true,
          studentEvalViewingEnabled: true,
        },
      },
    }, { pause: !sessionUserId });

  const reviewableMemberships = useMemo(() => (
    Array.isArray(userMemberships)
      ? userMemberships.filter((membership) =>
          membership?.hasFisdapReviewAccess === true ||
          String(membership?.primaryRole || "").trim().toLowerCase() === "instructor"
        )
      : []
  ), [userMemberships]);
  const reportMemberships = useMemo(() => (
    reviewableMemberships.filter((membership) => membership?.cohort?.studentEvalViewingEnabled === true)
  ), [reviewableMemberships]);
  const hasMembershipAccess = Array.isArray(userMemberships) && (
    studentOnly ? userMemberships.length > 0 : reportMemberships.length > 0
  );
  const hasFallbackCohortAccess = Boolean(studentOnly && sessionFallbackCohortId);
  const hasAccess =
    isAdmin ||
    (studentOnly
      ? (accessProfile?.cohortMemberships?.activeCount || 0) > 0
      : accessProfile?.clinicalDocs?.canReviewFisdap === true || accessProfile?.evaluations?.canView === true || hasMembershipAccess) ||
    hasFallbackCohortAccess;

  // Get accessible cohort IDs
  const accessibleCohortIds = useMemo(() => {
    if (isAdmin) return null; // Admin can see all
    const sourceMemberships = studentOnly ? (userMemberships || []) : reportMemberships;
    const membershipCohortIds = sourceMemberships.map((m) => m.cohortId).filter(Boolean);
    if (membershipCohortIds.length > 0) return membershipCohortIds;
    if (sessionFallbackCohortId) return [sessionFallbackCohortId];
    return [];
  }, [userMemberships, reportMemberships, isAdmin, sessionFallbackCohortId, studentOnly]);

  // Fetch cohorts
  const cohortFilter = useMemo(() => {
    const filter = { isActive: { equals: true } };
    if (!isAdmin) {
      // In student view, only show cohorts this user is associated with.
      if (studentOnly) {
        filter.id = { in: accessibleCohortIds || [] };
      } else if (accessibleCohortIds.length > 0) {
        filter.id = { in: accessibleCohortIds };
      }
    }
    return filter;
  }, [isAdmin, accessibleCohortIds, studentOnly]);

  const [{ data: cohorts, fetching: fetchingCohorts, error: cohortsError }] =
    useFindMany(api.cohort, {
      filter: cohortFilter,
      select: {
        id: true,
        displayName: true,
        name: true,
        institution: true,
      },
      sort: { displayName: "Ascending" },
    });

  useEffect(() => {
    if (!selectedCohortId && cohorts && cohorts.length > 0) {
      setSelectedCohortId(cohorts[0].id);
    }
  }, [cohorts, selectedCohortId]);

  // Fetch students in selected cohort
  const studentMembershipFilter = useMemo(() => {
    const filter = {
      cohortId: { equals: selectedCohortId },
      active: { equals: true },
      userId: { isSet: true }, // Only get memberships with valid users
      ...(studentOnly ? { userId: { equals: session?.user?.id } } : { primaryRole: { equals: "student" } }),
    };
    return filter;
  }, [selectedCohortId, studentOnly, session?.user?.id]);

  const [{ data: studentMemberships, fetching: fetchingStudents, error: studentMembershipsError }] = useFindMany(
    api.cohortMembership,
    {
      filter: studentMembershipFilter,
      select: {
        id: true,
        userId: true,
        userEmail: true,
        passingShiftCount: true,
        validLabShifts: true,
        validClinicalShifts: true,
        validFieldShifts: true,
        validTeamLeadShifts: true,
        totalValidShifts: true,
        totalLabSubmissions: true,
        totalClinicalSubmissions: true,
        totalFieldSubmissions: true,
        totalTeamLeadSubmissions: true,
        totalSubmissions: true,
        erroredShifts: true,
        totalShiftCount: true,
        lastShiftSubmittedAt: true,
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
      },
      sort: { userEmail: "Ascending" },
    },
    { pause: !selectedCohortId }
  );

  const sortedStudentMemberships = useMemo(() => {
    return sortStudentMemberships(studentMemberships || []);
  }, [studentMemberships]);

  const effectiveStudentMemberships = useMemo(() => {
    if (!studentOnly) return sortedStudentMemberships;
    if (sortedStudentMemberships.length > 0) return sortedStudentMemberships;
    if (!sessionUserId) return [];

    return [
      {
        id: `session-user-${sessionUserId}`,
        userId: sessionUserId,
        userEmail: session?.user?.email || "",
        passingShiftCount: 0,
        validLabShifts: 0,
        validClinicalShifts: 0,
        validFieldShifts: 0,
        validTeamLeadShifts: 0,
        totalValidShifts: 0,
        totalLabSubmissions: 0,
        totalClinicalSubmissions: 0,
        totalFieldSubmissions: 0,
        totalTeamLeadSubmissions: 0,
        totalSubmissions: 0,
        erroredShifts: 0,
        totalShiftCount: 0,
        user: {
          id: sessionUserId,
          firstName: session?.user?.firstName || "",
          lastName: session?.user?.lastName || "",
          email: session?.user?.email || "",
        },
      },
    ];
  }, [studentOnly, sortedStudentMemberships, sessionUserId, session?.user]);

  useEffect(() => {
    if (!selectedCohortId) {
      setDateRange({ from: "", to: "" });
      return;
    }

    if (selectedSemesterId && semesterDateRange) {
      setDateRange({
        from: toDateInputValue(semesterDateRange.startDate),
        to: toDateInputValue(semesterDateRange.endDate),
      });
    }
  }, [selectedCohortId, selectedSemesterId, semesterDateRange]);

  // Fetch shift processing records for students
  const studentIds = useMemo(() => {
    return effectiveStudentMemberships.map((m) => m.userId).filter(Boolean);
  }, [effectiveStudentMemberships]);

  const shiftFilter = useMemo(() => {
    const scopedStudentId =
      selectedPathologyStudent?.userId ||
      selectedStudentId ||
      (studentOnly ? studentIds[0] : null);

    if (!scopedStudentId) return {};

    return {
      submittedById: { equals: scopedStudentId },
    };
  }, [selectedPathologyStudent, selectedStudentId, studentIds, studentOnly]);

  const hasScopedDateFilter = Boolean(dateRange.from || dateRange.to);
  const needsAllShiftsQuery =
    Boolean(selectedCohortId) &&
    Boolean(shiftFilter.submittedById);
  const useStoredSummaries =
    !needsAllShiftsQuery ||
    (fetchingAllShifts && (!allShifts || allShifts.length === 0));

  // Fetch shifts for expanded student only
  const expandedStudentFilter = useMemo(() => {
    if (!expandedStudentId) return {};
    const filter = {
      submittedById: { equals: expandedStudentId }
    };
    return filter;
  }, [expandedStudentId]);

  useEffect(() => {
    let cancelled = false;

    const loadExpandedStudentShifts = async () => {
      if (!expandedStudentId) {
        setExpandedStudentShifts([]);
        setFetchingExpandedShifts(false);
        return;
      }

      setFetchingExpandedShifts(true);
      try {
        const records = await fetchAllShiftPages(expandedStudentFilter);
        if (!cancelled) {
          setExpandedStudentShifts(records);
        }
      } catch (error) {
        console.error("Failed to load expanded student shifts", error);
        if (!cancelled) {
          setExpandedStudentShifts([]);
        }
      } finally {
        if (!cancelled) {
          setFetchingExpandedShifts(false);
        }
      }
    };

    loadExpandedStudentShifts();

    return () => {
      cancelled = true;
    };
  }, [expandedStudentId, expandedStudentFilter]);

  const [{ data: relatedUserFiles, fetching: loadingRelatedFiles }] = useFindMany(
    api.userFile,
    {
      filter: selectedShift?.id
        ? {
            shiftProcessingId: { equals: selectedShift.id },
            status: { equals: "active" },
          }
        : undefined,
      select: {
        id: true,
        title: true,
        originalFilename: true,
        file: { url: true, fileName: true, mimeType: true },
      },
      first: 50,
    },
    { pause: !selectedShift?.id }
  );

  // Fetch all shifts for the selected cohort/date scope for accurate summary counts.
  useEffect(() => {
    let cancelled = false;

    const loadAllShifts = async () => {
      if (!needsAllShiftsQuery) {
        setAllShifts([]);
        setFetchingAllShifts(false);
        return;
      }

      setFetchingAllShifts(true);
      try {
        const records = await fetchAllShiftPages(shiftFilter);
        if (!cancelled) {
          setAllShifts(records);
        }
      } catch (error) {
        console.error("Failed to load cohort shifts", error);
        if (!cancelled) {
          setAllShifts([]);
        }
      } finally {
        if (!cancelled) {
          setFetchingAllShifts(false);
        }
      }
    };

    loadAllShifts();

    return () => {
      cancelled = true;
    };
  }, [needsAllShiftsQuery, shiftFilter]);

  const shifts = isPrinting ? allShifts : expandedStudentShifts;
  const fetchingShifts = isPrinting ? fetchingAllShifts : fetchingExpandedShifts;

  const dateScopedShifts = useMemo(() => {
    if (!shifts) return [];
    return applyDateRangeFilter(shifts, dateRange);
  }, [shifts, dateRange]);

  const countedShifts = useMemo(() => {
    return applyAdvancedShiftFilters(dateScopedShifts, false, false);
  }, [dateScopedShifts]);

  const visibleShifts = useMemo(() => {
    return applyAdvancedShiftFilters(dateScopedShifts, showDuplicates, showDrafts);
  }, [dateScopedShifts, showDuplicates, showDrafts]);

  const dateScopedAllShifts = useMemo(() => {
    if (!allShifts) return [];
    return applyDateRangeFilter(allShifts, dateRange);
  }, [allShifts, dateRange]);

  const countedAllShifts = useMemo(() => {
    return applyAdvancedShiftFilters(dateScopedAllShifts, false, false);
  }, [dateScopedAllShifts]);

  const hasPrintableShifts = useMemo(() => {
    if (studentOnly || selectedStudentId) {
      return dateScopedAllShifts.length > 0;
    }
    return Array.isArray(shifts) && shifts.length > 0;
  }, [studentOnly, selectedStudentId, dateScopedAllShifts, shifts]);

  // Counted shifts reconcile against schedule; submissions track every record in scope.
  const shiftSummary = useMemo(() => {
    const empty = { ...EMPTY_SHIFT_TYPE_COUNTS, totalCounted: 0, totalSubmissions: 0 };
    if (!selectedCohortId) return empty;

    if (useStoredSummaries) {
      const membershipsForCounts = selectedStudentId
        ? effectiveStudentMemberships.filter((m) => m.userId === selectedStudentId)
        : effectiveStudentMemberships;

      return membershipsForCounts.reduce((acc, membership) => {
        acc.lab += Number(membership.validLabShifts || 0);
        acc.clinical += Number(membership.validClinicalShifts || 0);
        acc.field += Number(membership.validFieldShifts || 0);
        acc.teamLead += Number(membership.validTeamLeadShifts || 0);
        acc.totalCounted += Number(membership.totalValidShifts || membership.totalShiftCount || 0);
        acc.totalSubmissions += Number(membership.totalSubmissions || membership.totalShiftCount || 0);
        return acc;
      }, empty);
    }

    const countedInScope = selectedStudentId
      ? countedAllShifts.filter((shift) => getShiftStudentId(shift) === selectedStudentId)
      : countedAllShifts;
    const submissionsInScope = selectedStudentId
      ? dateScopedAllShifts.filter((shift) => getShiftStudentId(shift) === selectedStudentId)
      : dateScopedAllShifts;

    countedInScope.forEach((shift) => {
      const key = getShiftTypeKey(shift);
      if (key) empty[key] += 1;
      empty.totalCounted += 1;
    });
    empty.totalSubmissions = submissionsInScope.length;

    return empty;
  }, [selectedCohortId, useStoredSummaries, effectiveStudentMemberships, countedAllShifts, dateScopedAllShifts, selectedStudentId]);

  const studentMetricsById = useMemo(() => {
    if (useStoredSummaries) {
      return effectiveStudentMemberships.reduce((metrics, membership) => {
        metrics[membership.userId] = {
          totalCounted: Number(membership.totalValidShifts ?? membership.totalShiftCount ?? 0),
          pass: Number(membership.passingShiftCount || 0),
          totalSubmissions: Number(membership.totalSubmissions || membership.totalShiftCount || 0),
          typeCounts: {
            lab: Number(membership.validLabShifts || 0),
            clinical: Number(membership.validClinicalShifts || 0),
            field: Number(membership.validFieldShifts || 0),
            teamLead: Number(membership.validTeamLeadShifts || 0),
            total: Number(membership.totalValidShifts || membership.totalShiftCount || 0),
          },
        };
        return metrics;
      }, {});
    }

    const metrics = {};
    for (const shift of countedAllShifts) {
      const studentId = getShiftStudentId(shift);
      if (!studentId) continue;
      if (!metrics[studentId]) {
        metrics[studentId] = {
          totalCounted: 0,
          pass: 0,
          totalSubmissions: 0,
          typeCounts: { ...EMPTY_SHIFT_TYPE_COUNTS },
        };
      }
      metrics[studentId].totalCounted += 1;
      if (isPassingShift(shift)) {
        metrics[studentId].pass += 1;
      }
      const key = getShiftTypeKey(shift);
      if (key) metrics[studentId].typeCounts[key] += 1;
      metrics[studentId].typeCounts.total += 1;
    }
    for (const shift of dateScopedAllShifts) {
      const studentId = getShiftStudentId(shift);
      if (!studentId) continue;
      if (!metrics[studentId]) {
        metrics[studentId] = {
          totalCounted: 0,
          pass: 0,
          totalSubmissions: 0,
          typeCounts: { ...EMPTY_SHIFT_TYPE_COUNTS },
        };
      }
      metrics[studentId].totalSubmissions += 1;
    }
    return metrics;
  }, [useStoredSummaries, effectiveStudentMemberships, countedAllShifts, dateScopedAllShifts]);

  // Map student memberships to display data
  const shiftsByStudent = useMemo(() => {
    const grouped = {};
    if (!effectiveStudentMemberships.length) return grouped;

    effectiveStudentMemberships.forEach((membership) => {
      if (selectedStudentId === "" || membership.userId === selectedStudentId) {
        // Get shifts only if this student is expanded or we're printing
        const studentShifts = (isPrinting && visibleShifts)
          ? visibleShifts.filter((shift) => getShiftStudentId(shift) === membership.userId)
          : (expandedStudentId === membership.userId && visibleShifts)
          ? visibleShifts
          : [];
        const countedStudentShifts = (isPrinting && countedShifts)
          ? countedShifts.filter((shift) => getShiftStudentId(shift) === membership.userId)
          : (expandedStudentId === membership.userId && countedShifts)
          ? countedShifts
          : [];
        const studentMetrics = studentMetricsById[membership.userId] || {
          totalCounted: Number(membership.totalValidShifts ?? membership.totalShiftCount ?? 0),
          pass: Number(membership.passingShiftCount || 0),
          totalSubmissions: Number(membership.totalSubmissions || membership.totalShiftCount || 0),
          typeCounts: {
            lab: Number(membership.validLabShifts || 0),
            clinical: Number(membership.validClinicalShifts || 0),
            field: Number(membership.validFieldShifts || 0),
            teamLead: Number(membership.validTeamLeadShifts || 0),
            total: Number(membership.totalValidShifts || membership.totalShiftCount || 0),
          },
        };

        const expandedStudentSummary =
          expandedStudentId === membership.userId && !fetchingExpandedShifts
            ? summarizeStudentShifts(countedStudentShifts)
            : null;

        grouped[membership.userId] = {
          membership,
          shifts: studentShifts,
          scopedTypeCounts: expandedStudentSummary?.typeCounts || studentMetrics.typeCounts,
          passCount: expandedStudentSummary?.pass ?? studentMetrics.pass,
          totalCount: expandedStudentSummary?.total ?? studentMetrics.totalCounted,
          submissionCount: studentMetrics.totalSubmissions,
        };
      }
    });

    return grouped;
  }, [effectiveStudentMemberships, selectedStudentId, visibleShifts, countedShifts, expandedStudentId, isPrinting, studentMetricsById, fetchingExpandedShifts]);

  // Calculate cohort stats from membership data
  const cohortStats = useMemo(() => {
    if (!selectedCohortId || !effectiveStudentMemberships.length) {
      return null;
    }

    const membershipsForStats = selectedStudentId
      ? effectiveStudentMemberships.filter((m) => m.userId === selectedStudentId)
      : effectiveStudentMemberships;

    const totalStudents = membershipsForStats.length;
    const totalShifts = membershipsForStats.reduce(
      (sum, m) => sum + (m.totalShiftCount || 0),
      0
    );
    
    // Get date range from lastShiftSubmittedAt if available
    const submissionDates = membershipsForStats
      .map(m => m.lastShiftSubmittedAt)
      .filter(d => d)
      .map(d => new Date(d))
      .filter(d => !isNaN(d));
    
    const minDate = submissionDates.length > 0 ? new Date(Math.min(...submissionDates)) : null;
    const maxDate = submissionDates.length > 0 ? new Date(Math.max(...submissionDates)) : null;

    return {
      totalStudents,
      totalShifts,
      dateRange:
        minDate && maxDate
          ? `${formatDate(minDate)} - ${formatDate(maxDate)}`
          : "No submissions",
    };
  }, [selectedCohortId, effectiveStudentMemberships, selectedStudentId]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  const toggleStudent = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
      setExpandedStudentId(null);
    } else {
      newExpanded.add(studentId);
      setExpandedStudentId(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const openShiftDetail = (shift) => {
    setSelectedShift(shift);
    onOpen();
  };

  const openPathologyCounter = (membership) => {
    const studentId = membership?.userId;
    if (!studentId) return;
    setSelectedPathologyStudent(membership);
    setPathologySemesterId("");
    onPathologyOpen();
  };

  const pathologyCountsForModal = useMemo(() => {
    if (!selectedPathologyStudent?.userId) return null;
    const allStudentShifts = (allShifts || [])
      .filter((shift) => getShiftStudentId(shift) === selectedPathologyStudent.userId)
      .filter((shift) => getShiftTypeKey(shift) !== "lab");
    const countedStudentShifts = selectLatestPassingShiftSubmissions(allStudentShifts);

    if (!pathologySemesterId) {
      return computeStudentPathologyCounts(countedStudentShifts);
    }

    const semester = semesters.find((s) => s.id === pathologySemesterId);
    if (!semester) return computeStudentPathologyCounts(countedStudentShifts);

    const semesterFrom = semester?.startDate ? new Date(semester.startDate).getTime() : null;
    const semesterTo = semester?.endDate ? new Date(semester.endDate).getTime() : null;
    const semesterFiltered = countedStudentShifts.filter((shift) => {
      const ts = toTimestamp(getShiftDate(shift) || getSubmissionDateValue(shift));
      if (!ts) return false;
      if (semesterFrom && ts < semesterFrom) return false;
      if (semesterTo && ts > semesterTo) return false;
      return true;
    });
    return computeStudentPathologyCounts(semesterFiltered);
  }, [selectedPathologyStudent, allShifts, pathologySemesterId, semesters]);

  if (!sessionUserId) {
    return (
      <Box p={8} display="flex" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (fetchingMemberships && !isAdmin) {
    return (
      <Box p={8} display="flex" justifyContent="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              {studentOnly
                ? "You are not assigned to an active student cohort."
                : "You do not have permission to view FISDAP reports. Please contact your administrator."}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={8} minH="100vh">
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" mb={2}>
          <HStack spacing={4}>
            <Button
              leftIcon={<ArrowBackIcon />}
              colorScheme="gray"
              variant="outline"
              onClick={() => navigate(studentOnly ? "/" : "/fisdap-review")}
              size="sm"
            >
              {studentOnly ? "Back to Dashboard" : "Back to Review"}
            </Button>
            <Heading size="lg">{studentOnly ? "ClinDocs Submissions" : "FISDAP Clinical Reports"}</Heading>
          </HStack>
        </HStack>

        {/* Filter Section */}
        <Box p={4} borderWidth={1} borderRadius="md" shadow="md" borderColor="gray.600">
          <VStack align="stretch" spacing={3}>
            <HStack spacing={3} wrap="wrap">
              {!studentOnly && (
                <>
                  <Box flex={1} minW="200px">
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Cohort
                    </Text>
                    <Select
                      size="sm"
                      placeholder="Select cohort"
                      value={selectedCohortId}
                      onChange={(e) => {
                        setSelectedCohortId(e.target.value);
                        setSelectedStudentId("");
                      }}
                      borderColor="gray.600"
                    >
                      {cohorts?.map((cohort) => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.displayName || cohort.name}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box flex={1} minW="200px">
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Semester
                    </Text>
                    <SemesterFilterDropdown
                      cohortId={selectedCohortId}
                      semesters={semesters}
                      selectedSemesterId={selectedSemesterId}
                      loading={semestersLoading}
                      onChange={(id, range) => {
                        setSelectedSemesterId(id);
                        if (!id || !range) {
                          setDateRange({ from: "", to: "" });
                          return;
                        }
                        setDateRange({
                          from: toDateInputValue(range.startDate),
                          to: toDateInputValue(range.endDate),
                        });
                      }}
                    />
                  </Box>
                </>
              )}

              {!studentOnly && (
              <Box flex={1} minW="200px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Student
                </Text>
                <Select
                  size="sm"
                  placeholder="Select student for detailed report"
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  isDisabled={!selectedCohortId}
                  borderColor="gray.600"
                >
                  {sortedStudentMemberships.map((membership) => (
                    <option key={membership.userId} value={membership.userId}>
                      {buildStudentDisplayName(membership)}
                      {membership.user?.email ? ` (${membership.user.email})` : ""}
                    </option>
                  ))}
                </Select>
              </Box>
              )}

              <Box flex={1} minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  From Date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={dateRange.from}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, from: e.target.value })
                  }
                />
              </Box>

              <Box flex={1} minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  To Date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={dateRange.to}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, to: e.target.value })
                  }
                />
              </Box>

              <Box alignSelf="flex-end">
                <Button size="sm" onClick={handleRefresh}>
                  Refresh
                </Button>
              </Box>

              <Box alignSelf="flex-end">
                <Button
                  colorScheme="purple"
                  size="sm"
                  onClick={handlePrint}
                  isDisabled={
                    !selectedCohortId ||
                    (!studentOnly && !selectedStudentId) ||
                    !hasPrintableShifts
                  }
                >
                  Print Report
                </Button>
              </Box>
            </HStack>
          </VStack>
        </Box>

        {/* Advanced Filters */}
        <Box>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            mb={2}
          >
            {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
          </Button>
          <Collapse in={showAdvancedFilters} animateOpacity>
            <Box p={4} borderWidth={1} borderRadius="md" bg="gray.800" borderColor="gray.600">
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Filter Options
                </Text>
                <Checkbox
                  isChecked={showDuplicates}
                  onChange={(e) => setShowDuplicates(e.target.checked)}
                >
                  <Text fontSize="sm">Show Duplicate Submissions</Text>
                </Checkbox>
                <Checkbox
                  isChecked={showDrafts}
                  onChange={(e) => setShowDrafts(e.target.checked)}
                >
                  <Text fontSize="sm">Show Draft Shifts</Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Counted totals always use the latest locked submission per shift. These toggles only change which raw submissions are shown in the detail rows.
                </Text>
              </VStack>
            </Box>
          </Collapse>
        </Box>

        {/* Cohort Stats Header */}
        {selectedCohortId && cohortStats && (
          <Box p={4} borderWidth={1} borderRadius="md" bg="blue.900" shadow="sm" borderColor="blue.700">
            <HStack mb={3}>
              <Text fontSize="sm" color="gray.300">Selected semester:</Text>
              <Badge colorScheme={selectedSemester ? "green" : "gray"}>
                {formatSemesterBadge(selectedSemester)}
              </Badge>
            </HStack>
            <HStack spacing={6} wrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Total Students
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {cohortStats.totalStudents}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Counted Shifts
                </Text>
                <Text fontSize="3xl" fontWeight="extrabold" color="green.400">
                  {shiftSummary.totalCounted}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Total Submissions
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {shiftSummary.totalSubmissions}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  Submission Range
                </Text>
                <Text fontSize="lg" fontWeight="medium">
                  {cohortStats.dateRange}
                </Text>
              </Box>
              <Box minW="260px">
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Counted Shift Mix
                </Text>
                <HStack spacing={2} wrap="wrap">
                  <Badge colorScheme="purple">Lab {shiftSummary.lab}</Badge>
                  <Badge colorScheme="blue">Clinical {shiftSummary.clinical}</Badge>
                  <Badge colorScheme="orange">Field {shiftSummary.field}</Badge>
                  <Badge colorScheme="teal">Team Lead {shiftSummary.teamLead}</Badge>
                </HStack>
              </Box>
            </HStack>
          </Box>
        )}

        {/* Error Display */}
        {studentMembershipsError && (
          <Alert status="warning">
            <AlertIcon />
            <Box>
              <AlertTitle>Data Issue Detected</AlertTitle>
              <AlertDescription>
                Some student records could not be loaded. This usually means there are orphaned cohort memberships. Contact your administrator.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Loading States */}
        {(fetchingCohorts || fetchingStudents || fetchingShifts) && (
          <Box display="flex" justifyContent="center" p={4}>
            <Spinner />
          </Box>
        )}

        {/* No Cohort Selected */}
        {!selectedCohortId && !fetchingCohorts && (
          <Alert status="info">
            <AlertIcon />
            Select a cohort to view reports
          </Alert>
        )}

        {!studentOnly && selectedCohortId && !selectedStudentId && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>Student Selection Recommended</AlertTitle>
              <AlertDescription>
                Cohort totals are using stored summary counts. Select a student to load a fully paginated, record-level report and avoid large cohort-wide queries.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* No Students Found */}
        {!studentOnly &&
          selectedCohortId &&
          !fetchingStudents &&
          effectiveStudentMemberships.length === 0 && (
            <Alert status="warning">
              <AlertIcon />
              No students found in this cohort
            </Alert>
          )}

        {/* Student Accordion */}
        {selectedCohortId &&
          effectiveStudentMemberships.length > 0 && (
            <Accordion allowMultiple>
              {Object.entries(shiftsByStudent).map(
                ([studentId, { membership, shifts: studentShifts, passCount, totalCount, submissionCount, scopedTypeCounts }]) => {
                  const passRate =
                    totalCount > 0
                      ? Math.round((passCount / totalCount) * 100)
                      : 0;

                  return (
                    <AccordionItem key={studentId} mb={2} borderRadius="md" shadow="sm" borderWidth={1} borderColor="gray.600">
                      <h2>
                        <AccordionButton onClick={() => toggleStudent(studentId)}>
                          <Box flex="1" textAlign="left">
                            <VStack align="start" spacing={1}>
                              <HStack spacing={3}>
                                <Text fontWeight="medium">
                                  {membership.user?.firstName && membership.user?.lastName
                                    ? `${membership.user.firstName} ${membership.user.lastName}`
                                    : membership.userEmail || 'Unknown Student'}
                                </Text>
                                <Badge colorScheme="blue">{totalCount} counted</Badge>
                                <Badge colorScheme="gray">{submissionCount} submissions</Badge>
                                {totalCount > 0 && (
                                  <Badge colorScheme="green">{passRate}% pass</Badge>
                                )}
                                <Button
                                  size="xs"
                                  colorScheme="purple"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPathologyCounter(membership);
                                  }}
                                >
                                  Pathology Counter
                                </Button>
                              </HStack>
                              <Text fontSize="sm" color="gray.600">
                                {membership.user?.email || membership.userEmail}
                              </Text>
                              {/* Shift count boxes */}
                              <HStack spacing={2} mt={2}>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Lab</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.lab || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Clinical</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.clinical || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Field</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.field || 0}</Text>
                                  </VStack>
                                </Box>
                                <Box bg="gray.600" px={2} py={1} borderRadius="md">
                                  <VStack spacing={0}>
                                    <Text fontSize="xs" color="gray.400">Team Lead</Text>
                                    <Text fontSize="sm" fontWeight="bold">{scopedTypeCounts.teamLead || 0}</Text>
                                  </VStack>
                                </Box>
                              </HStack>
                            </VStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        {fetchingExpandedShifts && expandedStudentId === studentId ? (
                          <Box display="flex" justifyContent="center" p={4}>
                            <Spinner size="sm" />
                          </Box>
                        ) : studentShifts.length === 0 ? (
                          <Alert status="info" size="sm">
                            <AlertIcon />
                            {submissionCount > 0
                              ? "No visible shifts in the current detail filter."
                              : "No shifts submitted yet"}
                          </Alert>
                        ) : (
                          <Table size="sm" variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Shift Date</Th>
                                <Th>Shift ID</Th>
                                <Th>Type</Th>
                                <Th width="50px" textAlign="center">Dup</Th>
                                <Th>Location</Th>
                                <Th>Pts</Th>
                                <Th width="40px" textAlign="center">🔒</Th>
                                <Th>Submitted</Th>
                                <Th>Status</Th>
                                <Th>Primary Impressions</Th>
                                <Th>Secondary Impressions</Th>
                                <Th>Actions</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {studentShifts.map((shift) => (
                                <Tr key={shift.id}>
                                  <Td>{formatDate(getShiftDate(shift))}</Td>
                                  <Td fontSize="xs">{shift.sourceShiftId}</Td>
                                  <Td>
                                    <Badge colorScheme="blue" variant="subtle">
                                      {getShiftTypeLabel(shift)}
                                    </Badge>
                                  </Td>
                                  <Td textAlign="center" fontSize="xs">
                                    {(() => {
                                      const dupInfo = getDuplicateInfo(shift, studentShifts);
                                      if (!dupInfo) return "-";
                                      if (dupInfo.isLatest) {
                                        return (
                                          <Badge colorScheme="green" size="xs">
                                            #{dupInfo.position} ✓
                                          </Badge>
                                        );
                                      }
                                      return (
                                        <Badge colorScheme="orange" size="xs">
                                          #{dupInfo.position}
                                        </Badge>
                                      );
                                    })()}
                                  </Td>
                                  <Td fontSize="sm">{getShiftLocation(shift)}</Td>
                                  <Td textAlign="center">
                                    <Badge colorScheme="purple" size="sm">
                                      {getPatientCount(shift)}
                                    </Badge>
                                  </Td>
                                  <Td textAlign="center" fontSize="lg">
                                    {isShiftLocked(shift) ? "🔒" : ""}
                                  </Td>
                                  <Td fontSize="xs">{formatDate(shift.submissionDate)}</Td>
                                  <Td>
                                    <StatusBadge verdict={shift.auditVerdict} />
                                  </Td>
                                  <Td fontSize="xs">
                                    {extractImpressions([shift], "primary_impression")
                                      .join(", ") || "N/A"}
                                  </Td>
                                  <Td fontSize="xs">
                                    {extractImpressions([shift], "secondary_impression")
                                      .join(", ") || "N/A"}
                                  </Td>
                                  <Td>
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      onClick={() => openShiftDetail(shift)}
                                    >
                                      Review
                                    </Button>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  );
                }
              )}
            </Accordion>
          )}
      </VStack>

      {/* Shift Detail Modal */}
      <ShiftDetailModal
        shift={selectedShift}
        relatedUserFiles={relatedUserFiles || []}
        loadingRelatedFiles={loadingRelatedFiles}
        isOpen={isOpen}
        onClose={onClose}
      />

      <Modal isOpen={isPathologyOpen} onClose={onPathologyClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Pathology Counter
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              {selectedPathologyStudent
                ? buildStudentDisplayName(selectedPathologyStudent)
                : "Student"}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={3}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Semester Scope
              </Text>
              <Select
                size="sm"
                value={pathologySemesterId}
                onChange={(e) => setPathologySemesterId(e.target.value)}
              >
                <option value="">All Semesters (Active Cohort)</option>
                {semesters.map((semester) => (
                  <option key={semester.id} value={semester.id}>
                    {semester.name}
                  </option>
                ))}
              </Select>
            </Box>
            <VStack align="stretch" spacing={2} maxH="60vh" overflowY="auto">
              {pathologyCountsForModal ? (
                <>
                  <HStack justify="space-between" p={2} borderWidth={1} borderRadius="md" bg="gray.800">
                    <Text fontSize="sm" fontWeight="semibold">Refusals (Valid Shift Patients)</Text>
                    <Badge colorScheme={pathologyCountsForModal.refusalCount > 0 ? "orange" : "gray"}>
                      {pathologyCountsForModal.refusalCount}
                    </Badge>
                  </HStack>
                  <HStack justify="space-between" p={2} borderWidth={1} borderRadius="md" bg="gray.700">
                    <Text fontSize="xs" color="gray.300">Pathology</Text>
                    <HStack spacing={4}>
                      <Text fontSize="xs" color="gray.300">Primary</Text>
                      <Text fontSize="xs" color="gray.300">Secondary</Text>
                    </HStack>
                  </HStack>
                  {STANDARD_PRIMARY_PATHOLOGIES
                    .map((label) => ({
                      label,
                      primary: pathologyCountsForModal.primaryCounts?.[label] || 0,
                      secondary: pathologyCountsForModal.secondaryCounts?.[label] || 0,
                    }))
                    .sort((a, b) => (b.primary + b.secondary) - (a.primary + a.secondary) || a.label.localeCompare(b.label))
                    .map(({ label, primary, secondary }) => (
                    <HStack key={label} justify="space-between" p={2} borderWidth={1} borderRadius="md">
                      <Text fontSize="sm">{label}</Text>
                      <HStack spacing={4}>
                        <Badge colorScheme={primary > 0 ? "purple" : "gray"}>{primary}</Badge>
                        <Badge colorScheme={secondary > 0 ? "cyan" : "gray"}>{secondary}</Badge>
                      </HStack>
                    </HStack>
                  ))}
                </>
              ) : (
                <Text color="gray.500">No pathology data available for this student.</Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPathologyClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hidden print view */}
      {isPrinting && (
        <Box
          id="printable-report"
          display="none"
          sx={{
            '@media print': {
              display: 'block !important',
            }
          }}
        >
          {/* Header - more compact */}
          <Box textAlign="center" mb={3} pb={2} borderBottom="2px solid black">
            <Heading size="md" mb={1}>
              {cohorts?.find(c => c.id === selectedCohortId)?.displayName} - Clinical Shift Report
            </Heading>
            {dateRange.from && dateRange.to && (
              <Text fontSize="xs" color="gray.600">
                {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
              </Text>
            )}
            <Text fontSize="xs" color="gray.600">
              Generated: {new Date().toLocaleDateString()}
            </Text>
          </Box>

          {/* Two-column layout for students */}
          <Box
            sx={{
              columnCount: 2,
              columnGap: '30px',
              fontSize: '10px',
            }}
          >
            {Object.entries(shiftsByStudent).map(
              ([studentId, { membership, shifts: studentShifts }], index) => {
                return (
                  <Box 
                    key={studentId} 
                    className="print-column" 
                    mb={3}
                    pb={2}
                    borderBottom={index < Object.keys(shiftsByStudent).length - 1 ? "1px solid #ccc" : "none"}
                    sx={{
                      breakInside: 'avoid',
                      pageBreakInside: 'avoid',
                    }}
                  >
                    {/* Student name header */}
                    <Text fontWeight="bold" fontSize="12px" mb={2} pb={1} borderBottom="1px solid #666">
                      {membership.user?.firstName && membership.user?.lastName
                        ? `${membership.user.firstName} ${membership.user.lastName}`
                        : membership.userEmail || 'Unknown Student'}
                    </Text>
                    
                    {/* Shift list */}
                    <Box as="ul" pl={3} mb={2} sx={{ listStyleType: 'none' }}>
                      {studentShifts
                        .sort((a, b) => new Date(getShiftDate(a)) - new Date(getShiftDate(b)))
                        .map((shift) => (
                          <Box as="li" key={shift.id} mb={1}>
                            <Text fontSize="9px" fontWeight="semibold" lineHeight="1.1" mb="2px">
                              {formatDate(getShiftDate(shift))} • {getShiftTypeLabel(shift)}
                            </Text>
                            <Text fontSize="8px" color="gray.600" pl={2} lineHeight="1.1" mb="2px">
                              ID: {shift.sourceShiftId}
                            </Text>
                            <Text fontSize="9px" pl={2} lineHeight="1.1">
                              {getShiftLocation(shift)} • {getPatientCount(shift)} pts •{' '}
                              {shift.auditVerdict === 'pass' ? '✓ Pass' : shift.auditVerdict === 'fail' ? '✗ Fail' : '? Pending'}
                              {isShiftLocked(shift) && ' 🔒'}
                            </Text>
                          </Box>
                        ))}
                    </Box>
                    
                    {/* Student summary stats */}
                    <Text fontSize="8px" color="gray.600" fontStyle="italic" pl={2}>
                      Totals: {studentShifts.length} shifts • {' '}
                      {studentShifts.reduce((sum, s) => sum + getPatientCount(s), 0)} patients • {' '}
                      Pass: {studentShifts.filter(s => s.auditVerdict === 'pass').length} • {' '}
                      Fail: {studentShifts.filter(s => s.auditVerdict === 'fail').length}
                    </Text>
                  </Box>
                );
              }
            )}
          </Box>
          
          <style>{printStyles}</style>
        </Box>
      )}
    </Box>
  );
}
